import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { Control, useController } from "react-hook-form";

import {
  ListTagsAndGroupsQuery,
  ListTagsAndGroupsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import { RhfSelect } from "~frontend/components/forms/react-hook-form-components";
import {
  LIST_TAGS_AND_GROUPS,
  ListTagsTag,
  ListTagsTagGroup,
} from "~frontend/graphql/tags";
import { palette } from "~frontend/theme";
import formatTagName from "~frontend/util/formatTagName";

import {
  nestedTagTableGetChildRows,
  nestedTagTableRenderName,
} from "../../tags/TagTable";
import { usePaginatedTags } from "../../tags/pagination";
import { ThreadFilterAndSortValues } from "./schema";

export type TagFilter = {
  tags: { id: string; name: string | null }[];
  filterOperation: "any" | "all";
};

export function TagFilterDialog({
  control,
  open,
  subaccountId,
  onClose,
  onApply,
}: {
  control: Control<ThreadFilterAndSortValues, any, ThreadFilterAndSortValues>;
  open: boolean;
  subaccountId: string;
  onClose: () => void;
  onApply: () => Promise<void>;
}): ReactElement {
  const { field } = useController({
    control,
    name: "tags",
  });

  const [selectedTags, setSelectedTags] = useState<
    { id: string; name: string | null }[]
  >(field.value);

  const [search, setSearch] = useState("");

  const { data, loading, error } = useQuery<
    ListTagsAndGroupsQuery,
    ListTagsAndGroupsQueryVariables
  >(LIST_TAGS_AND_GROUPS, {
    variables: {
      subaccountId,
    },
  });

  const filteredTags = usePaginatedTags({
    tags: data?.listTags ?? [],
    tagGroups: data?.listTagGroups ?? [],
    search,
  }).data;

  const handleClose = () => {
    // Discard changes
    onClose();
    setSearch("");
    setSelectedTags(field.value);
  };
  const handleApply = async () => {
    field.onChange(selectedTags);
    onClose();
    return onApply();
  };
  return (
    <Dialog open={open}>
      <DialogTitle>Filter by Tag</DialogTitle>
      <DialogContent sx={{ width: "578px" }}>
        <Box
          sx={{
            backgroundColor: palette.lightGray,
            padding: "20px",
            pt: "16px",
            height: "432px",
            overflow: "scroll",
          }}
        >
          <FlexBox alignItems="flex-end" gap="8px" sx={{ mb: 3 }}>
            <div>Show results that match</div>
            <RhfSelect control={control} name="tagFilterOperation" secondary>
              <MenuItem key="any" value="any">
                any
              </MenuItem>
              <MenuItem key="all" value="all">
                all
              </MenuItem>
            </RhfSelect>
            <div>of the following tags:</div>
          </FlexBox>

          <AsyncWrapper loading={loading} errors={error} data={data}>
            {(data: ListTagsAndGroupsQuery) => (
              <Box>
                <TextField
                  onChange={event => {
                    setSearch(event.target.value);
                  }}
                  sx={{
                    padding: 0,
                    pb: 1,
                    margin: 0,
                    width: "100%",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      border: `1px solid ${palette.midGray} !important`,
                      fontSize: "12px",
                    },
                  }}
                  value={search}
                  placeholder="Search for a tag"
                />
                <Box>
                  <Table<ListTagsTag | ListTagsTagGroup>
                    size="small"
                    rows={filteredTags}
                    selectedRows={selectedTags.map(t => t.id)}
                    onChangeSelectedRows={(tagIds: string[]) => {
                      const tagIdsSet = new Set(tagIds);
                      const newSelectedTags: { id: string; name: string }[] =
                        [];

                      data.listTags.forEach(tag => {
                        if (tagIdsSet.has(tag.id)) {
                          newSelectedTags.push({ id: tag.id, name: tag.name });
                        }
                      });
                      data.listTagGroups.forEach(group => {
                        group.tags.forEach(tag => {
                          if (tagIdsSet.has(tag.id)) {
                            newSelectedTags.push({
                              id: tag.id,
                              name: formatTagName({
                                name: tag.name,
                                group,
                              }),
                            });
                          }
                        });
                      });

                      setSelectedTags(newSelectedTags);
                    }}
                    columns={[
                      {
                        key: "tagName",
                        renderValue: nestedTagTableRenderName,
                      },
                    ]}
                    selectAllDisabled
                    noFooter
                    getChildRows={nestedTagTableGetChildRows}
                    childRowSelectionMode="childOnly"
                  />
                </Box>
              </Box>
            )}
          </AsyncWrapper>
        </Box>
      </DialogContent>
      <DialogActions>
        <FlexBox
          justifyContent="space-between"
          sx={{ width: "100%", padding: "20px" }}
        >
          <Button
            variant="contained"
            color="tertiary"
            onClick={() => setSelectedTags([])}
          >
            Clear Selections
          </Button>

          <FlexBox gap={1}>
            <Button variant="contained" color="tertiary" onClick={handleClose}>
              Cancel
            </Button>

            <Button
              type="submit"
              onClick={handleApply}
              color="primary"
              variant="contained"
            >
              Apply Filters
            </Button>
          </FlexBox>
        </FlexBox>
      </DialogActions>
    </Dialog>
  );
}
